import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from '@stencil/core'

import { Breakpoint, BreakpointObserver, BREAKPOINTS } from '../../utils/breakpoint-observer'
import Helpers from '../../utils/helpers'
import { CssClassMap } from '../../utils/interfaces'

/**
 * Nomes das áreas do cabeçalho.
 */
const HeaderSlotNames = {
  LOGO: 'logo',
  SIGNATURE: 'signature',
  LINKS: 'links',
  FUNCTIONS: 'functions',
  SEARCH_ICON: 'search-icon',
  SEARCH: 'search',
  ACCESS: 'access',
  MENU_TRIGGER: 'menu-trigger',
  CAPTION: 'caption',
  SUBCAPTION: 'subcaption',
} as const

/**
 * Tipo que representa os nomes das áreas do cabeçalho.
 */
type HeaderSlotName = (typeof HeaderSlotNames)[keyof typeof HeaderSlotNames]

/**
 * Tipo que representa as configurações de uma área do cabeçalho.
 */
type HeaderAreaConfig = {
  cssClassMap: CssClassMap
  breakpoints: { loose: Breakpoint[]; compact: Breakpoint[] }
  hasContent: boolean
  isVisible: boolean
  isActive?: boolean
  event?: { click?: (event: MouseEvent) => void; focus?: (event: FocusEvent) => void }
}

/**
 * Tipo que representa as áreas do cabeçalho.
 */
type HeaderArea = {
  [K in HeaderSlotName]: HeaderAreaConfig
}

/**
 * Tipo que representa as densidades do cabeçalho.
 */
type HeaderDensity = 'small' | 'medium' | 'large'

/**
 * ## Design System
 *
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/header?tab=designer).
 *
 * @slot logo - Slot para a logo do cabeçalho.
 * @slot signature - Slot para a assinatura visual da organização.
 * @slot links - Slot para links de navegação principais.
 * @slot functions - Slot para botões de ação no cabeçalho.
 * @slot search - Slot para o campo de busca.
 * @slot access - Slot para o botão de acesso ou autenticação.
 * @slot menu-trigger - Slot para o botão que abre o menu.
 * @slot caption - Slot para o título do cabeçalho.
 * @slot subcaption - Slot para o subtítulo do cabeçalho.
 */
@Component({
  tag: 'br-header',
  styleUrl: 'header.scss',
  shadow: true,
})
export class Header {
  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento do DOM associado ao componente.
   */
  @Element() el: HTMLBrHeaderElement

  /**
   * Texto exibido como título do cabeçalho.
   */
  @Prop({ reflect: true }) readonly caption: string = null

  /**
   * URL associada ao título do cabeçalho.
   */
  @Prop({ reflect: true }) readonly captionUrl: string = null

  /**
   * Identificador único do cabeçalho; gerado automaticamente quando omitido.
   */
  @Prop({ reflect: true }) readonly customId: string = Helpers.generateUniqueId()

  /**
   * Densidade do cabeçalho: controla espaçamento e proporções.
   */
  @Prop({ reflect: true }) readonly density: HeaderDensity = 'medium'

  /**
   * Ativa o modo compacto do cabeçalho.
   */
  @Prop({ reflect: true, mutable: true }) isCompact: boolean = false

  /**
   * Fixa o cabeçalho no topo durante a rolagem.
   */
  @Prop({ reflect: true }) readonly isSticky: boolean = false

  /**
   * Texto da assinatura exibida ao lado da logo.
   */
  @Prop({ reflect: true }) readonly signature: string = null

  /**
   * Define qual lista encolhe primeiro quando o espaço é limitado.
   */
  @Prop({ reflect: true }) readonly shrinkFirst: 'links' | 'functions' = 'functions'

  /**
   * Texto exibido como subtítulo do cabeçalho.
   */
  @Prop({ reflect: true }) readonly subcaption: string = null

  /**
   * URL associada ao subtítulo do cabeçalho.
   */
  @Prop({ reflect: true }) readonly subcaptionUrl: string = null

  /**
   * Representa as configurações da área do cabeçalho.
   */
  @State() headerArea: HeaderArea = {
    [HeaderSlotNames.LOGO]: {
      cssClassMap: {
        'header-logo': true,
      },
      breakpoints: {
        loose: [...BreakpointObserver.getBreakpoints('from', BREAKPOINTS.X_SMALL)],
        compact: [...BreakpointObserver.getBreakpoints('from', BREAKPOINTS.X_SMALL)],
      },
      hasContent: false,
      isVisible: false,
    },
    [HeaderSlotNames.SIGNATURE]: {
      cssClassMap: {
        'header-signature': true,
      },
      breakpoints: {
        loose: [...BreakpointObserver.getBreakpoints('from', BREAKPOINTS.SMALL)],
        compact: [],
      },
      hasContent: Boolean(this.signature && this.signature.trim().length > 0),
      isVisible: Boolean(this.signature && this.signature.trim().length > 0),
    },
    [HeaderSlotNames.LINKS]: {
      cssClassMap: {
        'header-links': true,
      },
      breakpoints: {
        loose: [...BreakpointObserver.getBreakpoints('from', BREAKPOINTS.X_SMALL)],
        compact: [...BreakpointObserver.getBreakpoints('from', BREAKPOINTS.X_SMALL)],
      },
      hasContent: false,
      isVisible: false,
    },
    [HeaderSlotNames.FUNCTIONS]: {
      cssClassMap: {
        'header-functions': true,
      },
      breakpoints: {
        loose: [...BreakpointObserver.getBreakpoints('from', BREAKPOINTS.X_SMALL)],
        compact: [...BreakpointObserver.getBreakpoints('from', BREAKPOINTS.X_SMALL)],
      },
      hasContent: false,
      isVisible: false,
    },
    [HeaderSlotNames.SEARCH_ICON]: {
      cssClassMap: {
        'header-search-icon': true,
      },
      breakpoints: {
        loose: [...BreakpointObserver.getBreakpoints('until', BREAKPOINTS.MEDIUM)],
        compact: [...BreakpointObserver.getBreakpoints('from', BREAKPOINTS.X_SMALL)],
      },
      hasContent: false,
      isVisible: false,
      event: {
        click: this.handleSearchIconClick.bind(this),
        focus: this.handleSearchIconFocus.bind(this),
      },
    },
    [HeaderSlotNames.SEARCH]: {
      cssClassMap: {
        'header-search': true,
      },
      breakpoints: {
        loose: [...BreakpointObserver.getBreakpoints('from', BREAKPOINTS.LARGE)],
        compact: [],
      },
      hasContent: false,
      isVisible: false,
      isActive: false,
    },
    [HeaderSlotNames.ACCESS]: {
      cssClassMap: {
        'header-access': true,
      },
      breakpoints: {
        loose: [...BreakpointObserver.getBreakpoints('from', BREAKPOINTS.X_SMALL)],
        compact: [...BreakpointObserver.getBreakpoints('from', BREAKPOINTS.X_SMALL)],
      },
      hasContent: false,
      isVisible: false,
    },
    [HeaderSlotNames.MENU_TRIGGER]: {
      cssClassMap: {
        'header-menu-trigger': true,
      },
      breakpoints: {
        loose: [...BreakpointObserver.getBreakpoints('from', BREAKPOINTS.X_SMALL)],
        compact: [...BreakpointObserver.getBreakpoints('from', BREAKPOINTS.X_SMALL)],
      },
      hasContent: false,
      isVisible: false,
    },
    [HeaderSlotNames.CAPTION]: {
      cssClassMap: {
        'header-caption': true,
      },
      breakpoints: {
        loose: [...BreakpointObserver.getBreakpoints('from', BREAKPOINTS.X_SMALL)],
        compact: [...BreakpointObserver.getBreakpoints('from', BREAKPOINTS.X_SMALL)],
      },
      hasContent: false,
      isVisible: false,
    },
    [HeaderSlotNames.SUBCAPTION]: {
      cssClassMap: {
        'header-subcaption': true,
      },
      breakpoints: {
        loose: [...BreakpointObserver.getBreakpoints('from', BREAKPOINTS.SMALL)],
        compact: [...BreakpointObserver.getBreakpoints('from', BREAKPOINTS.SMALL)],
      },
      hasContent: false,
      isVisible: false,
    },
  }

  /**
   * Evento disparado quando o cabeçalho entra ou sai do modo compacto.
   * O evento contém os detalhes do estado compacto e o ID do componente pai.
   * @event headerCompactChange
   */
  @Event({
    eventName: 'headerCompactChange',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
  headerCompactChange: EventEmitter<{ parentId: string; isCompact: boolean }>

  /**
   * Evento disparado para indicar qual lista deve encolher primeiro.
   * O evento contém os detalhes do ID do componente pai e o nome da lista.
   * @event headerWidthChange
   */
  @Event({
    eventName: 'headerWidthChange',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
  headerWidthChange: EventEmitter<{ id: string; listNames: ('links' | 'functions')[]; shrink: boolean }>

  /**
   * Observa mudanças na propriedade isCompact.
   * @param newValue O novo valor de isCompact.
   */
  @Watch('isCompact')
  watchIsCompact(newValue: boolean) {
    this.headerCompactChange.emit({ parentId: this.customId, isCompact: newValue })
    this.verifyListSpace(this.shrinkFirst)
  }

  /**
   * Observador de mudanças de breakpoint.
   */
  private breakpointObserver: BreakpointObserver

  /**
   * Indica o breakpoint atual.
   */
  private currentBreakpoint: Breakpoint

  /**
   * Armazena a posição de scroll do elemento pai.
   */
  private parentScrollTop = 0

  /**
   * Timeout para o evento de redimensionamento.
   */
  private resizeTimeout: number = null

  /**
   * Listener para o evento 'headerListUpdate', disparado pelos componentes filhos 'br-header-list'.
   * O evento contém o ID do componente pai e o nome da lista.
   */
  @Listen('headerListUpdate', { target: 'document', passive: true })
  private handleHeaderListUpdate(event: CustomEvent<{ id: string; listName: string }>) {
    if (event.detail.id === this.customId) {
      requestAnimationFrame(() => {
        switch (event.detail.listName) {
          case 'functions':
            this.verifyListSpace('links')
            break
          case 'links':
            this.verifyListSpace('functions')
            break
        }
      })
    }
  }

  /**
   * Listener para o evento 'resize' da janela.
   * Dispara o evento de redimensionamento do cabeçalho após um breve atraso de 100ms
   */
  @Listen('resize', { target: 'window', passive: true })
  private handleListResize() {
    clearTimeout(this.resizeTimeout)
    this.resizeTimeout = window.setTimeout(() => {
      this.headerWidthChange.emit({ id: this.customId, listNames: ['links', 'functions'], shrink: false })
    }, 100)
  }

  /**
   * Ciclo de vida do componente.
   * Executado quando o componente é conectado ao DOM.
   */
  connectedCallback() {
    this.setBreakpointObserver()
    this.syncHeaderAreaStates()
    this.setScrollOnParent()
  }

  /**
   * Ciclo de vida do componente.
   * Executado uma vez após o componente ser totalmente carregado ao DOM.
   */
  componentDidLoad() {
    this.monitorSlotContent()
    this.headerCompactChange.emit({ parentId: this.customId, isCompact: this.isCompact })
    this.verifyListSpace(this.shrinkFirst)

    void this.handleHeaderListUpdate
    void this.handleListResize
  }

  /**
   * Ciclo de vida do componente.
   * Executado quando o componente é desconectado do DOM.
   */
  disconnectedCallback() {
    this.unsetScrollOnParent()
    this.breakpointObserver.unsubscribe(this.handleBreakpointChange.bind(this))
    this.breakpointObserver.destroy()
    clearTimeout(this.resizeTimeout)
  }

  /**
   * Reinicializa o estado das listas do cabeçalho, disparando o evento de redimensionamento.
   * Pode ser chamado externamente para forçar a atualização das listas.
   */
  @Method()
  async resetHeaderList() {
    this.handleListResize()
  }

  /**
   * Manipula as mudanças de breakpoint.
   * @param breakpoint O breakpoint ativo.
   */
  private handleBreakpointChange(breakpoint: Breakpoint) {
    this.currentBreakpoint = breakpoint
    Object.keys(this.headerArea).forEach((area) => {
      const name = area as HeaderSlotName
      this.controlHeaderAreaUpdateStates(name)
    })
  }

  /**
   * Manipula o evento de scroll no elemento pai.
   */
  private handleParentScroll = () => {
    const parent = this.el.parentElement
    if (parent == null) return

    const atTop = parent.scrollTop === 0
    const shouldBeCompact = this.isSticky && !atTop

    if (this.isCompact !== shouldBeCompact) {
      this.parentScrollTop = parent.scrollTop
      this.isCompact = shouldBeCompact
      requestAnimationFrame(() => {
        parent.scrollTop = this.parentScrollTop
      })
      this.handleBreakpointChange(this.currentBreakpoint)
    }
  }

  /**
   * Manipula o clique no botão de fechar busca.
   */
  private handleSearchCloseClick = () => {
    this.updateHeaderArea(HeaderSlotNames.SEARCH, {
      isActive: false,
      isVisible: this.headerArea[HeaderSlotNames.SEARCH]?.breakpoints[
        `${this.isCompact ? 'compact' : 'loose'}`
      ]?.includes(this.currentBreakpoint),
    })
  }

  /**
   * Manipula o clique no ícone de busca.
   */
  private handleSearchIconClick() {
    this.updateHeaderArea(HeaderSlotNames.SEARCH, {
      isVisible: true,
      isActive: true,
    })
  }

  /**
   * Manipula o foco no ícone de busca.
   */
  private handleSearchIconFocus() {
    this.el.shadowRoot.querySelectorAll('slot[name="links"], slot[name="functions"]').forEach((slot: Element) => {
      const brHeaderList = (slot as HTMLSlotElement)
        .assignedElements()
        .find((el) => el.tagName.toLowerCase() === 'br-header-list')
      if (brHeaderList != null) {
        ;(brHeaderList as HTMLBrHeaderListElement).closeList()
      }
    })
  }

  /**
   *
   * Controla a atualização dos estados da área do cabeçalho.
   * @param name O nome da área do cabeçalho.
   */
  private controlHeaderAreaUpdateStates(name: HeaderSlotName) {
    const state = this.headerArea[name]?.breakpoints[`${this.isCompact ? 'compact' : 'loose'}`]?.includes(
      this.currentBreakpoint
    )
    switch (name) {
      case HeaderSlotNames.SEARCH:
        this.updateHeaderArea(name, {
          isVisible: state,
          isActive: state ? null : this.headerArea[name]?.isActive,
        })
        break
      default:
        this.updateHeaderArea(name, {
          isVisible: state,
        })
    }
  }

  /**
   * Retorna um mapa de classes CSS para o componente.
   * @returns Um objeto contendo as classes CSS a serem aplicadas.
   */
  private getCssClassMap(): CssClassMap {
    return {
      'br-header': true,
      compact: this.isCompact,
      small: this.density === 'small' && !this.isCompact,
      large: this.density === 'large' && !this.isCompact,
    }
  }

  /**
   * Verifica se um valor é um nome de slot de cabeçalho válido.
   * @param value O valor a ser verificado.
   * @returns Um valor booleano indicando se o valor é um nome de slot de cabeçalho válido.
   */
  private isHeaderSlotName(value: string): value is HeaderSlotName {
    return Object.values(HeaderSlotNames).includes(value as HeaderSlotName)
  }

  /**
   * Monitora as mudanças de conteúdo dos slots.
   */
  private monitorSlotContent() {
    this.el.shadowRoot.querySelectorAll('slot').forEach((slot) => {
      slot.addEventListener('slotchange', () => {
        const slotName = slot.name
        if (this.isHeaderSlotName(slotName)) {
          this.updateHeaderArea(slotName, { hasContent: slot.assignedNodes().length > 0 })
        }
      })
    })
  }

  /**
   * Configura o observador de mudanças de breakpoint.
   */
  private setBreakpointObserver() {
    this.breakpointObserver = new BreakpointObserver()
    this.breakpointObserver.subscribe(this.handleBreakpointChange.bind(this))
  }

  /**
   * Configura o evento de scroll no elemento pai.
   */
  private setScrollOnParent() {
    const parent = this.el.parentElement
    if (this.isSticky && parent !== null) {
      parent.addEventListener('scroll', this.handleParentScroll)
      this.handleParentScroll()
    }
  }

  /**
   * Atualiza o estado da área do cabeçalho com base no conteúdo dos slots.
   */
  private syncHeaderAreaStates() {
    this.el.querySelectorAll('[slot]').forEach((element) => {
      const slotName = element.slot
      if (this.isHeaderSlotName(slotName)) {
        this.headerArea[slotName].hasContent = true
        this.headerArea[slotName].isVisible = this.headerArea[slotName]?.breakpoints[
          `${this.isCompact ? 'compact' : 'loose'}`
        ]?.includes(this.currentBreakpoint)
      }
    })
  }

  /**
   * Remove scroll event listener from parent element.
   */
  private unsetScrollOnParent() {
    const parent = this.el.parentElement
    if (this.isSticky && parent !== null) {
      parent.removeEventListener('scroll', this.handleParentScroll)
    }
  }

  /**
   * Atualiza a área do cabeçalho.
   * @param name O nome da área do cabeçalho.
   * @param updates As atualizações a serem aplicadas.
   */
  private updateHeaderArea(name: HeaderSlotName, updates: Partial<HeaderAreaConfig>) {
    this.headerArea = {
      ...this.headerArea,
      [name]: { ...this.headerArea[name], ...updates },
    }
  }

  /**
   * Verifica o espaço disponível para uma lista específica no cabeçalho.
   * @param listName O nome da lista a ser verificada.
   */
  private verifyListSpace(listName: 'links' | 'functions') {
    let style, paddingLeft, paddingRight, totalWidth, bottomLeftWidth, topLeftWidth, topRightWidth, availableWidth
    if (this.isCompact) {
      style = window.getComputedStyle(this.el)
      paddingLeft = parseFloat(style.paddingLeft) || 0
      paddingRight = parseFloat(style.paddingRight) || 0
      totalWidth = this.el.clientWidth - paddingLeft - paddingRight
      bottomLeftWidth = this.el.shadowRoot.querySelector('.header-bottom-left')?.clientWidth || 0
      topRightWidth = this.el.shadowRoot.querySelector('.header-top-right')?.clientWidth || 0
      availableWidth = totalWidth - bottomLeftWidth
    } else {
      totalWidth = this.el.shadowRoot.querySelector('.header-top')?.clientWidth || 0
      topLeftWidth = this.el.shadowRoot.querySelector('.header-top-left')?.clientWidth || 0
      topRightWidth = this.el.shadowRoot.querySelector('.header-top-right')?.clientWidth || 0
      availableWidth = totalWidth - topLeftWidth
    }

    const isOverflowing = topRightWidth > availableWidth
    if (isOverflowing) {
      this.headerWidthChange.emit({ id: this.customId, listNames: [listName], shrink: true })
    }
  }

  /**
   * Renderiza o conteúdo padrão do título.
   * @returns Um elemento JSX representando o conteúdo padrão do título.
   */
  private renderDefaultCaptionContent() {
    return typeof this.caption === 'string' && this.caption.trim().length > 0 ? (
      typeof this.captionUrl === 'string' && this.captionUrl.trim().length > 0 ? (
        <a href={this.captionUrl} class="caption-link">
          {this.caption}
        </a>
      ) : (
        <span class="caption-text">{this.caption}</span>
      )
    ) : null
  }

  /**
   * Renderiza o conteúdo padrão da assinatura.
   * @returns Um elemento JSX representando o conteúdo padrão da assinatura.
   */
  private renderDefaultSignatureContent() {
    return typeof this.signature === 'string' && this.signature.trim().length > 0 ? (
      <span class="signature-text">{this.signature}</span>
    ) : null
  }

  /**
   * Renderiza o conteúdo padrão do subtítulo.
   * @returns Um elemento JSX representando o conteúdo padrão do subtítulo.
   */
  private renderDefaultSubcaptionContent() {
    return typeof this.subcaption === 'string' && this.subcaption.trim().length > 0 ? (
      typeof this.subcaptionUrl === 'string' && this.subcaptionUrl.trim().length > 0 ? (
        <a href={this.subcaptionUrl} class="subcaption-link">
          {this.subcaption}
        </a>
      ) : (
        <span class="subcaption-text">{this.subcaption}</span>
      )
    ) : null
  }

  /**
   * Renderiza o divisor entre as áreas do cabeçalho.
   * @param mainArea A área principal do cabeçalho.
   * @param secondaryAreas As áreas secundárias do cabeçalho.
   * @returns Um elemento JSX representando o divisor entre as áreas do cabeçalho.
   */
  private renderDivider(mainArea: HeaderSlotName, secondaryAreas: HeaderSlotName[] = []) {
    const isVisible =
      this.headerArea[mainArea]?.hasContent &&
      this.headerArea[mainArea]?.isVisible &&
      secondaryAreas.some((area) => this.headerArea[area]?.hasContent && this.headerArea[area]?.isVisible)
    return <br-divider orientation="vertical" class="header-divider" hidden={!isVisible}></br-divider>
  }

  /**
   * Renderiza a área do cabeçalho.
   * @param name O nome da área do cabeçalho.
   * @returns Um elemento JSX representando a área do cabeçalho.
   */
  private renderHeaderArea(name: HeaderSlotName) {
    return (
      <div
        class={this.headerArea[name]?.cssClassMap}
        hidden={!this.headerArea[name]?.isVisible && !this.headerArea[name]?.isActive}
        is-active={this.headerArea[name]?.isActive}
        onClick={this.headerArea[name]?.event?.click}
        onFocusin={this.headerArea[name]?.event?.focus}
      >
        <slot name={name}>{this.renderSlotDefaultContent(name)}</slot>
        {name === HeaderSlotNames.SEARCH && this.headerArea[name]?.isActive && this.renderSearchCloseButton()}
      </div>
    )
  }

  /**
   * Renderiza o botão de fechar busca.
   * @returns Um elemento JSX representando o botão de fechar busca.
   */
  private renderSearchCloseButton() {
    return (
      <br-button
        class="search-close"
        shape="circle"
        density="small"
        emphasis="tertiary"
        onClick={this.handleSearchCloseClick}
        aria-label="Fechar busca"
      >
        <br-icon iconName="fa7-solid:close"></br-icon>
      </br-button>
    )
  }

  /**
   * Renderiza o conteúdo padrão do slot.
   * @param name O nome do slot.
   * @returns Um elemento JSX representando o conteúdo padrão do slot.
   */
  private renderSlotDefaultContent(name: HeaderSlotName) {
    switch (name) {
      case HeaderSlotNames.CAPTION:
        return this.renderDefaultCaptionContent()
      case HeaderSlotNames.SUBCAPTION:
        return this.renderDefaultSubcaptionContent()
      case HeaderSlotNames.SIGNATURE:
        return this.renderDefaultSignatureContent()
      default:
        return null
    }
  }

  /**
   * Renderiza o componente.
   * @returns JSX.Element
   */
  render() {
    return (
      <Host>
        <header id={this.customId} class={this.getCssClassMap()}>
          <div class="container-lg">
            <div class="header-top">
              <div class="header-top-left">
                {this.renderHeaderArea(HeaderSlotNames.LOGO)}
                {this.renderDivider(HeaderSlotNames.LOGO, [HeaderSlotNames.SIGNATURE])}
                {this.renderHeaderArea(HeaderSlotNames.SIGNATURE)}
              </div>
              <div class="header-top-right">
                {this.renderHeaderArea(HeaderSlotNames.LINKS)}
                {this.renderDivider(HeaderSlotNames.LINKS, [HeaderSlotNames.FUNCTIONS, HeaderSlotNames.SEARCH_ICON])}
                {this.renderHeaderArea(HeaderSlotNames.FUNCTIONS)}
                {this.renderDivider(HeaderSlotNames.FUNCTIONS, [HeaderSlotNames.SEARCH_ICON])}
                {this.renderHeaderArea(HeaderSlotNames.SEARCH_ICON)}
                {this.renderHeaderArea(HeaderSlotNames.ACCESS)}
              </div>
            </div>
            <div class="header-bottom">
              <div class="header-bottom-left">
                {this.renderHeaderArea(HeaderSlotNames.MENU_TRIGGER)}
                <div class="header-caption-wrapper">
                  {this.renderHeaderArea(HeaderSlotNames.CAPTION)}
                  {this.renderHeaderArea(HeaderSlotNames.SUBCAPTION)}
                </div>
              </div>
              <div
                class={{
                  'header-bottom-right': true,
                  'is-active': this.headerArea[HeaderSlotNames.SEARCH]?.isActive,
                }}
              >
                {this.renderHeaderArea(HeaderSlotNames.SEARCH)}
              </div>
            </div>
          </div>
        </header>
      </Host>
    )
  }
}
